import React from 'react';
import {
  CustomTableColumns,
  DateRangeFilter,
  ETableFilterTypes,
  FilterValue,
  TableSortInfo,
} from './constants';

// Cell values are usually primitives, but a column can hold a ReactNode (e.g. a
// status badge or a link) — sort/filter need the rendered text, not
// String(reactElement), which stringifies to "[object Object]".
export const getCellText = (value: unknown): string => {
  if (value == null || typeof value === 'boolean') {
    return '';
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map(getCellText).join(' ');
  }
  if (React.isValidElement(value)) {
    return getCellText((value.props as { children?: unknown })?.children);
  }
  return String(value);
};

// True for any column whose values should be treated as dates (single Date or
// DateRange), used for chronological sorting.
const isDateFilterType = (filterType?: ETableFilterTypes) =>
  filterType === ETableFilterTypes.Date || filterType === ETableFilterTypes.DateRange;

// Parse a date string to a local Date. ISO date-only strings (yyyy-mm-dd, as
// emitted by FmlxDatePicker) are anchored to local midnight so day comparisons
// are not shifted by the UTC offset; other formats (e.g. locale "M/D/YYYY" cell
// values) fall back to the native parser, which is already local.
const parseToLocalDate = (value: string): Date | null => {
  if (!value) {
    return null;
  }
  const isoDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const date = new Date(isoDateOnly ? `${value}T00:00:00` : value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isSameLocalDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const compareSortValues = (valA: unknown, valB: unknown): number => {
  if (valA instanceof Date && valB instanceof Date) {
    return valA.getTime() - valB.getTime();
  }

  if (typeof valA === 'number' && typeof valB === 'number') {
    return valA - valB;
  }

  return getCellText(valA).localeCompare(getCellText(valB));
};

export const sortPreviewData = (
  data: any[],
  sortInfo: TableSortInfo,
  columns: CustomTableColumns[] = [],
) => {
  if (!Array.isArray(data)) {
    return [];
  }
  if (!sortInfo) {
    return data;
  }

  const { column, direction } = sortInfo;
  const isDateColumn = columns.some(
    (col) => col.key === column && isDateFilterType(col.filterType),
  );

  return [...data].sort((a, b) => {
    let valA = a?.[column];
    let valB = b?.[column];

    if (valA == null) {
      return 1;
    }
    if (valB == null) {
      return -1;
    }

    if (isDateColumn) {
      valA = new Date(valA);
      valB = new Date(valB);
    }

    const comparison = compareSortValues(valA, valB);
    return direction === 'asc' ? comparison : -comparison;
  });
};

export const getValidFilterValues = (columnFilters: Record<string, FilterValue>) => {
  return Object.entries(columnFilters).filter(([, f]) => {
    if (!f || f.value == null) {
      return false;
    }
    switch (f.type) {
      case ETableFilterTypes.Date:
        return !!f.value; // a single date has been picked
      case ETableFilterTypes.DateRange: {
        const { start, end } = f.value as DateRangeFilter;
        return !!start && !!end; // only apply if BOTH exist
      }
      default:
        return f.value !== '';
    }
  });
};

export const getProcessedFilters = (filters: [string, FilterValue][]) => {
  return filters.map(([colKey, filter]) => {
    switch (filter.type) {
      case ETableFilterTypes.Date: {
        return {
          colKey,
          type: filter.type,
          date: parseToLocalDate(String(filter.value)),
        };
      }

      case ETableFilterTypes.DateRange: {
        const { start, end } = filter.value as DateRangeFilter;

        const startTime = new Date(start);
        const endTime = new Date(end);

        return {
          colKey,
          type: filter.type,
          startTime,
          endTime,
        };
      }

      default: {
        const normalized = String(filter.value).toLowerCase().trim();
        return {
          colKey,
          type: filter.type,
          normalized,
        };
      }
    }
  });
};

export const getFilteredData = (data, processedFilters) => {
  return data.filter((row) => {
    return processedFilters.every((filter) => {
      const cell = row[filter.colKey];
      if (!cell) {
        return false;
      }

      switch (filter.type) {
        case ETableFilterTypes.Date: {
          const cellDate = parseToLocalDate(String(cell));
          return !!cellDate && !!filter.date && isSameLocalDay(cellDate, filter.date);
        }

        case ETableFilterTypes.DateRange: {
          const time = new Date(cell);
          if (!time) {
            return false;
          }

          return time >= filter.startTime && time <= filter.endTime;
        }

        default: {
          const value = getCellText(cell).toLowerCase();
          return value.includes(filter.normalized);
        }
      }
    });
  });
};
